import { supabase, hasSupabaseAuth } from '@/integrations/supabase/client';
import { Player } from '@/types/tournament';
import { DbPlayer, DbTournament, DbMatch, DbTournamentParticipant, PlayerStats, TournamentWithStats } from '@/types/stats';

export class StatsService {
  private hasAuth(): boolean {
    return hasSupabaseAuth();
  }
  // Player management
  async findOrCreatePlayer(name: string, phone?: string): Promise<DbPlayer> {
    // First try to find existing player by name
    const { data: existingPlayer } = await supabase
      .from('players')
      .select('*')
      .eq('name', name)
      .maybeSingle();

    if (existingPlayer) {
      return {
        ...existingPlayer,
        level: (existingPlayer.level || 'level_1') as any,
        reliability: existingPlayer.reliability ?? 0.5,
      };
    }

    // Create new player
    const { data: newPlayer, error } = await supabase
      .from('players')
      .insert({
        name,
        phone,
        rating: 1500.0,
        rating_deviation: 350.0,
        volatility: 0.06,
        reliability: 0.5,
        level: 'level_1',
        games_played: 0,
        wins: 0,
        losses: 0
      })
      .select()
      .single();

    if (error) throw error;
    return {
      ...newPlayer,
      level: (newPlayer.level || 'level_1') as any,
      reliability: newPlayer.reliability ?? 0.5,
    };
  }

  async getAllPlayers(): Promise<PlayerStats[]> {
    const { data: players, error } = await supabase
      .from('players')
      .select('*')
      .order('rating', { ascending: false });

    if (error) throw error;

    return players.map(player => this.enrichPlayerWithStats({
      ...player,
      level: (player.level || 'level_1') as any,
      reliability: player.reliability ?? 0.5,
    }));
  }

  private enrichPlayerWithStats(player: DbPlayer): PlayerStats {
    const win_rate = player.games_played > 0 ? player.wins / player.games_played : 0;
    
    // Calculate confidence interval (95% confidence)
    const margin = 1.96 * player.rating_deviation;
    const confidence_interval: [number, number] = [
      Math.max(0, player.rating - margin),
      player.rating + margin
    ];

    // Estimate level based on rating
    let level_estimate = 'Beginner';
    if (player.rating >= 2000) level_estimate = 'Expert';
    else if (player.rating >= 1700) level_estimate = 'Advanced';
    else if (player.rating >= 1400) level_estimate = 'Intermediate';

    // Simple recent form calculation (would need match history for real implementation)
    const recent_form: 'up' | 'down' | 'stable' = 
      player.rating_deviation < 150 ? 'stable' : 
      player.rating > 1500 ? 'up' : 'down';

    return {
      ...player,
      win_rate,
      confidence_interval,
      level_estimate,
      recent_form
    };
  }

  // Tournament management
  async createTournament(
    name: string, 
    type: 'mexicano' | 'americano' | 'team_americano', 
    totalRounds: number,
    participants: Player[]
  ): Promise<DbTournament> {
    // If not authenticated with Supabase, skip persistence to avoid RLS errors
    if (!this.hasAuth()) {
      console.warn('Skipping createTournament: no Supabase auth session');
      const now = new Date().toISOString();
      return {
        id: crypto.randomUUID(),
        name,
        tournament_type: type,
        total_rounds: totalRounds,
        completed_rounds: 0,
        is_completed: false,
        created_at: now,
        completed_at: undefined,
      };
    }

    const { data: tournament, error } = await supabase
      .from('tournaments')
      .insert({
        name,
        tournament_type: type,
        total_rounds: totalRounds,
        completed_rounds: 0,
        is_completed: false
      })
      .select()
      .single();

    if (error) throw error;

    // Add participants
    for (const participant of participants) {
      const dbPlayer = await this.findOrCreatePlayer(participant.name, participant.phone);
      
      await supabase
        .from('tournament_participants')
        .insert({
          tournament_id: tournament.id,
          player_id: dbPlayer.id,
          final_points: 0
        });
    }

    return {
      ...tournament,
      tournament_type: tournament.tournament_type as 'mexicano' | 'americano' | 'team_americano'
    };
  }

  async getTournaments(): Promise<TournamentWithStats[]> {
    const { data: tournaments, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        tournament_participants(count),
        matches(count)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return tournaments.map(tournament => ({
      ...tournament,
      tournament_type: tournament.tournament_type as 'mexicano' | 'americano' | 'team_americano',
      participants_count: tournament.tournament_participants?.[0]?.count || 0,
      matches_count: tournament.matches?.[0]?.count || 0,
      avg_rating: 1500 // Would calculate from actual participants
    }));
  }

  // Match recording
  async recordMatch(
    tournamentId: string,
    roundNumber: number,
    courtNumber: number,
    team1Player1: string,
    team1Player2: string,
    team2Player1: string,
    team2Player2: string,
    team1Score: number,
    team2Score: number,
    winnerTeam: 1 | 2
  ): Promise<DbMatch> {
    // If not authenticated with Supabase, skip persistence
    if (!this.hasAuth()) {
      console.warn('Skipping recordMatch: no Supabase auth session');
      return {
        id: crypto.randomUUID(),
        tournament_id: tournamentId,
        round_number: roundNumber,
        court_number: courtNumber,
        team1_player1_id: 'local',
        team1_player2_id: 'local',
        team2_player1_id: 'local',
        team2_player2_id: 'local',
        team1_score: team1Score,
        team2_score: team2Score,
        winner_team: winnerTeam,
        created_at: new Date().toISOString(),
      };
    }

    // Find or create players
    const [p1, p2, p3, p4] = await Promise.all([
      this.findOrCreatePlayer(team1Player1),
      this.findOrCreatePlayer(team1Player2),
      this.findOrCreatePlayer(team2Player1),
      this.findOrCreatePlayer(team2Player2)
    ]);

    const { data: match, error } = await supabase
      .from('matches')
      .insert({
        tournament_id: tournamentId,
        round_number: roundNumber,
        court_number: courtNumber,
        team1_player1_id: p1.id,
        team1_player2_id: p2.id,
        team2_player1_id: p3.id,
        team2_player2_id: p4.id,
        team1_score: team1Score,
        team2_score: team2Score,
        winner_team: winnerTeam as 1 | 2
      })
      .select()
      .single();

    if (error) throw error;

    // Trigger rating update via Edge Function
    try {
      await supabase.functions.invoke('update-ratings', {
        body: { match_id: match.id }
      });
    } catch (ratingError) {
      console.error('Failed to update ratings:', ratingError);
      // Continue execution even if rating update fails
    }

    return {
      ...match,
      winner_team: match.winner_team as 1 | 2
    };
  }

  async finalizeTournament(tournamentId: string, finalStandings: { playerId: string; position: number; points: number }[]): Promise<void> {
    if (!this.hasAuth()) {
      console.warn('Skipping finalizeTournament: no Supabase auth session');
      return;
    }
    // Update tournament as completed
    await supabase
      .from('tournaments')
      .update({
        is_completed: true,
        completed_at: new Date().toISOString()
      })
      .eq('id', tournamentId);

    // Update participant final standings
    for (const standing of finalStandings) {
      await supabase
        .from('tournament_participants')
        .update({
          final_position: standing.position,
          final_points: standing.points
        })
        .eq('tournament_id', tournamentId)
        .eq('player_id', standing.playerId);
    }
  }
}
